/**
 * Snowflakes Component
 *
 * Renders falling snowflakes on the sidebar and topbar when Christmas mode is enabled.
 * Main content area stays clear of snowflakes.
 * Uses CSS animations for performance.
 */

import { useEffect, useState } from 'react';
import '../styles/Snowflakes.css';

const Snowflakes = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    // Check localStorage and class on mount
    const checkChristmasMode = () => {
      const activeTheme = localStorage.getItem('activeTheme');
      const fromStorage = activeTheme === 'christmas' || localStorage.getItem('christmasMode') === 'true';
      const fromClass = document.documentElement.classList.contains('christmas');
      setIsEnabled(fromStorage || fromClass);
    };

    checkChristmasMode();

    // Listen for storage changes (when toggled from settings)
    const handleStorageChange = (e) => {
      if (e.key === 'activeTheme') {
        setIsEnabled(e.newValue === 'christmas');
      }
      // Legacy support
      if (e.key === 'christmasMode') {
        setIsEnabled(e.newValue === 'true');
      }
    };

    // Also listen for class changes on html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsEnabled(document.documentElement.classList.contains('christmas'));
        }
      });
    });

    // Watch for sidebar collapse state
    const sidebarObserver = new MutationObserver(() => {
      const sidebar = document.querySelector('.dashboard-sidebar');
      if (sidebar) {
        setIsCollapsed(sidebar.classList.contains('collapsed'));
      }
    });

    const sidebar = document.querySelector('.dashboard-sidebar');
    if (sidebar) {
      sidebarObserver.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
      setIsCollapsed(sidebar.classList.contains('collapsed'));
    }

    observer.observe(document.documentElement, { attributes: true });
    window.addEventListener('storage', handleStorageChange);

    return () => {
      observer.disconnect();
      sidebarObserver.disconnect();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (!isEnabled) return null;

  // Generate snowflakes for sidebar
  const sidebarSnowflakes = Array.from({ length: 25 }, (_, i) => ({
    id: `sidebar-${i}`,
    left: `${Math.random() * 100}%`,
    animationDuration: `${12 + Math.random() * 16}s`,
    animationDelay: `${-Math.random() * 28}s`,
    opacity: 0.5 + Math.random() * 0.5,
    size: 3 + Math.random() * 4,
  }));

  // Generate snowflakes for topbar (same rate, scaled for shorter distance)
  const topbarSnowflakes = Array.from({ length: 20 }, (_, i) => ({
    id: `topbar-${i}`,
    left: `${Math.random() * 100}%`,
    animationDuration: `${1.2 + Math.random() * 1.6}s`,
    animationDelay: `${-Math.random() * 2.8}s`,
    opacity: 0.5 + Math.random() * 0.5,
    size: 2 + Math.random() * 3,
  }));

  return (
    <>
      {/* Sidebar snowflakes */}
      <div
        className={`snowflakes-container ${isCollapsed ? 'collapsed' : ''}`}
        aria-hidden="true"
      >
        {sidebarSnowflakes.map((flake) => (
          <div
            key={flake.id}
            className="snowflake"
            style={{
              left: flake.left,
              animationDuration: flake.animationDuration,
              animationDelay: flake.animationDelay,
              opacity: flake.opacity,
              width: `${flake.size}px`,
              height: `${flake.size}px`,
            }}
          />
        ))}
      </div>

      {/* Topbar snowflakes */}
      <div
        className={`snowflakes-topbar ${isCollapsed ? 'collapsed' : ''}`}
        aria-hidden="true"
      >
        {topbarSnowflakes.map((flake) => (
          <div
            key={flake.id}
            className="snowflake snowflake-topbar"
            style={{
              left: flake.left,
              animationDuration: flake.animationDuration,
              animationDelay: flake.animationDelay,
              opacity: flake.opacity,
              width: `${flake.size}px`,
              height: `${flake.size}px`,
            }}
          />
        ))}
      </div>
    </>
  );
};

export default Snowflakes;
